import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { JWPlayer } from './player/jw-player'
import { YouTubePlayer } from './player/yt-player'
import { DistroScale } from './player/distroscale'
import { SendToNews } from './player/send-to-news'
import { AbstractPlayer } from './player/abstract-player'
import {
    JWPlayerConfigEvent,
    VideoProviderType,
    YTPlayerConfigEvent
} from './types'
import { EventType } from 'constants/enums'

export class SinglePlayer extends ComponentBase {
    private player: AbstractPlayer
    private fallbackElement: Nullable<HTMLElement>
    private controlButton: Nullable<HTMLButtonElement>

    public constructor(private element: HTMLElement) {
        super()
        const provider = element.dataset.provider || ''
        const lazy = element.dataset.lazyBootstrap || ''

        if (provider === VideoProviderType.JwPlayer) {
            if (lazy === 'True') {
                this.listenToInit()
            } else {
                void this.createJWPlayer()
            }
        } else if (provider === VideoProviderType.YouTube) {
            void this.createYouTubePlayer()
        } else if (provider === VideoProviderType.DistroScale) {
            this.player = new DistroScale(this.element)
        } else if (provider === VideoProviderType.SendToNews) {
            this.player = new SendToNews(this.element)
        }

        this.fallbackElement = this.getElement(
            '.video-fallback',
            this.element.parentElement
        )

        this.controlButton = this.getElement(
            '.video-controls',
            this.element.parentElement?.parentElement
        )
    }

    private createJWPlayer(): void {
        const events: JWPlayerConfigEvent = {
            onReady: () => this.onReady(),
            onError: () => this.onError(),
            onPlay: () => this.onPlay(),
            onPause: () => this.onPause()
        }

        this.player = new JWPlayer(this.element, events)
    }

    private async createYouTubePlayer(): Promise<void> {
        const events: YTPlayerConfigEvent = {
            onReady: () => this.onReady(),
            onError: () => this.onError(),
            onPlay: () => this.onPlay(),
            onPause: () => this.onPause()
        }

        this.player = new YouTubePlayer(this.element, events)
    }

    private setPlayerButton(): void {
        if (this.player.autoplay) {
            this.setPlayerButtonPlaying()
        } else {
            this.setPlayerButtonPaused()
        }

        if (this.controlButton) {
            this.controlButton.addEventListener(EventType.CLICK, () => {
                if (this.player.getState() == 'playing') {
                    this.player.pause()
                    this.setPlayerButtonPaused()
                } else {
                    this.player.play()
                    this.setPlayerButtonPlaying()
                }
            })
        }
    }

    private setPlayerButtonPlaying(): void {
        if (this.controlButton) {
            this.controlButton.classList.remove('video-controls--paused')
            this.controlButton.classList.add('video-controls--playing')
            this.controlButton.innerHTML = 'Pause Video'
        }
    }

    private setPlayerButtonPaused(): void {
        if (this.controlButton) {
            this.controlButton.classList.remove('video-controls--playing')
            this.controlButton.classList.add('video-controls--paused')
            this.controlButton.innerHTML = 'Play Video'
        }
    }

    /**
     * Due to Promise getter resolving before data travels between components.
     * can potentially be fixed with using Timeout on Promise or RXJS Observable.
     */
    private onReady(): void {
        // if button on lazy jw bootstrap clicked play
        if (this.element.dataset.lazyBootstrap === 'True') {
            const wrapper = document.querySelector('.embedded-video')
            wrapper.classList.remove('dedicated-video')
            this.player.play()
        }

        const title = this.player.title
        const titleEl = this.getElement('.control-heading--max')
        if (title && titleEl) {
            titleEl.innerHTML = title
        }
        this.setPlayerButton()
    }

    private onError(): void {
        if (this.fallbackElement) {
            this.fallbackElement.style.display = 'flex'
        }
    }

    private onPlay(): void {
        this.player.pauseAllOther()
        this.setPlayerButtonPlaying()
    }

    private onPause(): void {
        this.setPlayerButtonPaused()
    }

    private listenToInit(): void {
        const btn = this.getElement('.dedicated-video__btn')

        if (btn) {
            // Use the named event handler function
            btn.addEventListener(EventType.CLICK, this.handleButtonClick)
        }
    }

    private handleButtonClick = (event) => {
        this.bootstrapJW(event.target)
    }

    private bootstrapJW(btn): void {
        // Remove the event listener
        btn.removeEventListener(EventType.CLICK, this.handleButtonClick)
        // Remove bootstrap button
        btn.remove()
        // Init JW Player (load all required for playing)
        void this.createJWPlayer()
    }
}
